import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useGenerateCardProxy, useRemainingGenerations } from '@/hooks/useGenerateCardProxy';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { logAiCardCustomization } from '@/utilities/ga';
import { motion } from 'framer-motion';
import { Baby, Camera, Cog, Edit, Grid, Leaf, Paintbrush, Palette, Skull, Sparkles, Star, Wand2, Zap } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCart } from '../hooks/useCart';
import { useUserCredits } from '../hooks/useCredits';
import { useUserStore } from '../hooks/useUser';
import Badge from './Badge';
import CardImage from './CardImage';
import CreditPurchaseModal from './CreditPurchaseModal';
import YouTubeEmbed from './YouTubeEmbed';
import { BorderTrail } from './ui/border-trail';
import { RainbowButton } from './ui/rainbow-button';

interface CartContextType {
	cartOpened: boolean;
	openCart: () => void;
	closeCart: () => void;
	cart: Array<{ quantity: number; details: any }>;
	updateQuantity: (cardId: string, quantity: number) => void;
	addCardToCart: (card: any) => void;
	clearCart: () => void;
	getNumberOfItemsInCart: () => number;
	getQuantityOfItem: (cardId: string) => number;
	addCardsToCart: (cards: Array<{ quantity: number; details: any }>) => void;
}

interface CustomizeCardModalProps {
	card: any;
	onCustomize: (customImage: string) => void;
}

export default function CustomizeCardModal({ card, onCustomize }: CustomizeCardModalProps) {
	const [customText, setCustomText] = useState('');
	const [generatedImage, setGeneratedImage] = useState<string | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(true);
	const [isTextareaExpanded, setIsTextareaExpanded] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
	const { customerDetails, isProMemberActive, user } = useUserStore();
	const cart = useCart() as CartContextType | null;
	const isPro = isProMemberActive;
	const { t } = useTranslation();

	const { mutate: generateCardProxy, isPending: isGenerating, cancel } = useGenerateCardProxy();
	const { data: remainingGenerations } = useRemainingGenerations(customerDetails?.stripeCustomerId);
	const { data: creditInfo, refetch: refetchCredits, isLoading: isLoadingCredits } = useUserCredits();

	// Helper function to apply a prompt - always replace text in textarea
	const applyPrompt = (promptKey: string) => {
		const promptText = t(`customizeCardModal.prompts.${promptKey}`);
		// Always replace the text
		setCustomText(promptText);
		// Expand the textarea when a prompt is clicked
		setIsTextareaExpanded(true);
	};

	const handleGenerate = () => {
		if (!isPro || !cart || !customerDetails?.email || !user?.uid) return;

		// Check if user has any credits available
		if (creditInfo && creditInfo.totalCredits <= 0) {
			setShowCreditPurchaseModal(true);
			return;
		}

		// Hide the editing interface when generating
		setIsEditing(false);
		// Start loading animation
		setIsLoading(true);

		// Track AI card customization
		logAiCardCustomization({
			cardId: card.id,
			customizationType: customText.substring(0, 50), // Send a shortened version of the customization text
			additionalParams: {
				card_name: card.name,
				card_set: card.set,
				prompt_length: customText.length,
				user_id: user.uid
			}
		});

		generateCardProxy(
			{
				card,
				cardId: card.id,
				originalImage: card.image_uris?.normal || card.image_uris?.large,
				customText,
				userEmail: customerDetails.email,
				userUid: user.uid,
			},
			{
				onSuccess: (data) => {
					setGeneratedImage(data.cardImageUrl);
					onCustomize(data.cardImageUrl);
					// Stop loading animation
					setIsLoading(false);
					// Refresh credit information
					refetchCredits();
				},
				onError: (error) => {
					// Check if the error is due to cancellation
					if (error instanceof Error && error.message === 'cancelled') {
						// Don't show an error toast for cancelled requests
						// We don't show a toast here because we already show one when the cancel button is clicked
					} else {
						console.error('Error generating custom card:', error);
						toast.error(error instanceof Error ? error.message : t('customizeCardModal.error.failed'));
					}
					// If there's an error, go back to editing mode
					setIsEditing(true);
					// Stop loading animation
					setIsLoading(false);
				},
			}
		);
	};

	const handleAddToList = () => {
		if (!generatedImage || !cart) return;

		// Create custom card object with unique ID
		const customCard = {
			...card,
			image_uris: {
				...card.image_uris,
				large: generatedImage,
			},
			isCustom: true,
			customImage: generatedImage,
			originalCardId: card.id,
			id: `${card.id}-custom-${Date.now()}`,
		};

		// Track adding custom card to print list
		logAiCardCustomization({
			cardId: card.id,
			customizationType: 'added_to_print_list',
			additionalParams: {
				card_name: card.name,
				card_set: card.set,
				custom_card_id: customCard.id,
				user_id: user?.uid || 'unknown'
			}
		});

		cart.addCardToCart(customCard);

		setIsOpen(false);
	};

	if (!isPro) {
		return (
			<Dialog>
				<DialogTrigger asChild>
					<RainbowButton className='w-full'>
						<Wand2 className='w-4 h-4 mr-2' />
						{t('pro.promotion.customizeTitle')}
					</RainbowButton>
				</DialogTrigger>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('pro.promotion.customizeTitle')}</DialogTitle>
						<DialogDescription>{t('pro.promotion.customizeDescription')}</DialogDescription>
					</DialogHeader>
					<div className='flex justify-center py-4 flex-col gap-6'>
						<YouTubeEmbed videoId='cWPxMkSoD24' title='YouTube Video Demonstration' className='shadow-lg' />
						<RainbowButton asChild>
							<Link href='/pro-membership'>
								{t('common.upgrade')} {t('common.proMembership')}
							</Link>
						</RainbowButton>
						<p className='text-center text-sm text-muted-foreground'>
							<Link href='/ai' className='hover:underline'>
								Learn more about AI customization
							</Link>
						</p>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={(open) => {
					setIsOpen(open);
					// Reset to editing mode when modal is closed
					if (!open) {
						// Cancel the ongoing request if it's still running when modal is closed
						if (isGenerating) {
							cancel();
							toast.info(t('customizeCardModal.cancelledGeneration') || 'Generation cancelled');
							// Reset loading state
							setIsLoading(false);
						}
						setIsEditing(true);
						// Reset generated image when modal is closed
						setGeneratedImage(null);
						// Reset textarea to collapsed state
						setIsTextareaExpanded(false);
						// Clear the text
						setCustomText('');
					} else {
						// Refetch credits when modal is opened
						refetchCredits();
					}
				}}
			>
				<DialogTrigger asChild>
					<RainbowButton className='w-full'>
						<Wand2 className='w-4 h-4 mr-2' />
						{t('customizeCardModal.buttonText')}
					</RainbowButton>
				</DialogTrigger>
				<DialogContent className='sm:max-w-[600px] max-h-[85vh] overflow-y-auto'>
					<DialogHeader>
						<DialogTitle className='flex items-center flex-col md:flex-row gap-2'>
							{t('customizeCardModal.title')}
							<div className='flex gap-2'>
								<Badge>Beta</Badge>
								{creditInfo !== undefined && (
									<Badge>{creditInfo.totalCredits === 1 ? t('customizeCardModal.remainingGenerationsOne', { count: creditInfo.totalCredits }) : t('customizeCardModal.remainingGenerationsOther', { count: creditInfo.totalCredits })}</Badge>
								)}
							</div>
						</DialogTitle>
						<DialogDescription>{t('customizeCardModal.description')}</DialogDescription>
						<p className='text-sm text-muted-foreground mt-1'>
							View all your customized cards on your{' '}
							<Link href='/account' className='text-primary hover:underline'>
								My Account
							</Link>{' '}
							page.
						</p>
					</DialogHeader>
					<div>
						<div className='grid gap-4'>
							<div className='grid gap-2'>
								{isEditing ? (
									<motion.div key='editing' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
										<Textarea
											placeholder={t('customizeCardModal.placeholder')}
											value={customText}
											onChange={(e) => {
												setCustomText(e.target.value);
												// If user starts typing, expand the textarea
												if (e.target.value.length > 50 && !isTextareaExpanded) {
													setIsTextareaExpanded(true);
												} else if (e.target.value.length <= 50 && isTextareaExpanded) {
													setIsTextareaExpanded(false);
												}
											}}
											rows={isTextareaExpanded ? 3 : 1}
											className={`transition-all duration-300 ${isTextareaExpanded ? 'min-h-[100px]' : 'min-h-[40px]'}`}
										/>

										<div className='mt-2'>
											<div className='flex justify-between items-center mb-2'>
												<p className='text-sm font-medium'>{t('customizeCardModal.promptIdeas')}</p>
											</div>

											{/* Style Prompts */}
											<div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2'>
												{/* Art Styles */}
												<Button size='sm' variant='outline' onClick={() => applyPrompt('fantasy')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Sparkles className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.fantasyShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('scifi')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Wand2 className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.scifiShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('horror')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Skull className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.horrorShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('anime')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Star className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.animeShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('realistic')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Camera className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.realisticShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('watercolor')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Paintbrush className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.watercolorShort') || 'Watercolor'}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('pixelart')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Grid className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.pixelartShort') || 'Pixel Art'}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('oilpainting')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Palette className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.oilpaintingShort') || 'Oil Painting'}
												</Button>

												{/* Themes */}
												<Button size='sm' variant='outline' onClick={() => applyPrompt('seasonal')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Leaf className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.seasonalShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('steampunk')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Cog className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.steampunkShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('chibi')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Baby className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.chibiShort')}
												</Button>
												<Button size='sm' variant='outline' onClick={() => applyPrompt('cyberpunk')} className='flex items-center justify-start h-auto py-2 px-3 hover:bg-accent hover:text-accent-foreground transition-colors'>
													<div className='flex items-center justify-center bg-primary/10 rounded-full p-1 mr-2'>
														<Zap className='w-3 h-3 text-primary' />
													</div>
													{t('customizeCardModal.prompts.cyberpunkShort') || 'Cyberpunk'}
												</Button>
											</div>
										</div>

										{/* Generate button & buttons row */}
										<div className='flex flex-col sm:flex-row gap-2 justify-between mt-4 w-full'>
											{creditInfo && creditInfo.totalCredits <= 0 ? (
												<CreditPurchaseModal trigger={<RainbowButton className='w-full'>{t('customizeCardModal.buyCredits', 'Buy Credits')}</RainbowButton>} onSuccessfulPurchase={() => refetchCredits()} />
											) : (
												<RainbowButton onClick={handleGenerate} disabled={isGenerating || !customText.trim() || (creditInfo && creditInfo.totalCredits === 0)} className='w-full'>
													{isGenerating ? t('customizeCardModal.generatingButton') : t('customizeCardModal.generateButton')}
												</RainbowButton>
											)}
										</div>
									</motion.div>
								) : isLoading ? (
									<motion.div key='generating' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className='relative'>
										<div className='p-4 bg-muted rounded-md relative flex justify-between'>
											{isLoading && (
												<BorderTrail
													className={cn('bg-[linear-gradient(90deg,hsl(var(--color-1)),hsl(var(--color-5)),hsl(var(--color-3)),hsl(var(--color-4)),hsl(var(--color-2)))] transition-opacity duration-300', isLoading ? 'opacity-100' : 'opacity-0')}
													size={120}
												/>
											)}
											<p className='text-sm max-w-[400px]'>{customText.length > 100 ? `${customText.substring(0, 100)}...` : customText}</p>
											<Button
												size='sm'
												variant='ghost'
												onClick={() => {
													// Cancel the ongoing request if it's still running
													if (isGenerating) {
														cancel();
														toast.info(t('customizeCardModal.cancelledGeneration') || 'Generation cancelled');
														// Reset loading state
														setIsLoading(false);
													}
													setIsEditing(true);
												}}
											>
												<Edit className='w-4 h-4 mr-1' />
												{isGenerating ? 'Cancel' : 'Edit'}
											</Button>
										</div>
										<p className='text-center text-sm mt-4 text-muted-foreground'>{t('customizeCardModal.generationWaitMessage', 'This process may take a minute or two. Thanks for your patience!')}</p>
									</motion.div>
								) : (
									generatedImage && (
										<div>
											<motion.div key='success' initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
												<div className='p-4 bg-primary/10 rounded-md relative flex justify-between items-center'>
													<p className='text-sm max-w-[400px]'>{customText.length > 100 ? `${customText.substring(0, 100)}...` : customText}</p>
													<Button
														size='sm'
														variant='ghost'
														onClick={() => {
															setIsEditing(true);
														}}
													>
														<Edit className='w-4 h-4 mr-1' />
														{t('common.edit')}
													</Button>
												</div>
												<div className='mt-4 flex justify-center'>
													<RainbowButton type='submit' onClick={handleAddToList}>
														{t('customizeCardModal.addToPrintListButton')}
													</RainbowButton>
												</div>
											</motion.div>
										</div>
									)
								)}
							</div>
						</div>

						{(!isEditing || generatedImage) && (
							<motion.div className='mt-4' initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.3 }}>
								{card.image_uris?.large && (
									<div className={`grid grid-cols-2 gap-4`}>
										<div>
											<p className='text-sm font-medium mb-2'>Original</p>
											<CardImage src={card.image_uris.large} alt={`Original ${card.name}`} className='w-full' />
										</div>
										{generatedImage && (
											<div>
												<p className='text-sm font-medium mb-2'>Generated</p>
												<CardImage src={generatedImage} alt={`Generated ${card.name}`} className='w-full' />
											</div>
										)}
									</div>
								)}
							</motion.div>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Separate Credit Purchase Modal that shows automatically when needed */}
			{showCreditPurchaseModal && (
				<CreditPurchaseModal
					trigger={<div style={{ display: 'none' }} />}
					onSuccessfulPurchase={() => {
						refetchCredits();
						setShowCreditPurchaseModal(false);
					}}
				/>
			)}
		</>
	);
}
